import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import Image from "next/image";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import type { AccommodationFacilityType } from "../../../../../__generated__/gql/graphql";
import { useGtm } from "../../../../../lib/google-tag-manager/gtm";
import { CheckboxIcon } from "../../ui/checkbox-icon";

import {
  architectureFilterItems,
  ARCHITECTURE_LARGE_VALUE,
  isLargeFilter,
  facilityFilterItems,
} from "./_hooks/accommodation-filter";
import type { AreaItem } from "./accommodation-filter-context";

/** エリアの表示順序（slug）。この一覧にないエリアは末尾に追加される。 */
// prettier-ignore
const AREA_DISPLAY_ORDER = [
  "yamanakako", "kawaguchiko", "nasu",
  "karuizawa", "kitakaruizawa", "yatsugatake",  
  "minami-alps", "tateshina", "shirakabako",
  "hakuba", "azumino", "ichinomiya",
  "tateyama", "isumi", "izukogen",
  "shimoda",  "ashigara", "niseko",
  "amamioshima", "awajishima",
] as const;

function areaDisplayIndex(slug: string): number {
  const i = (AREA_DISPLAY_ORDER as readonly string[]).indexOf(slug);
  return i === -1 ? AREA_DISPLAY_ORDER.length : i;
}

function sortAreaItemsByDisplayOrder(items: AreaItem[]): AreaItem[] {
  return [...items].sort(
    (a, b) => areaDisplayIndex(a.slug) - areaDisplayIndex(b.slug),
  );
}

export function useFilterModal({
  areaItems,
  areaFilter,
  setAreaFilter,
  facilityFilter,
  setFacilityFilter,
  architectureFilter,
  setArchitectureFilter,
}: {
  areaItems: AreaItem[];
  areaFilter: Set<string>;
  setAreaFilter: (filter: Set<string>) => void;
  facilityFilter: Set<AccommodationFacilityType>;
  setFacilityFilter: (filter: Set<AccommodationFacilityType>) => void;
  architectureFilter: Set<string>;
  setArchitectureFilter: (filter: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);

  const openModal = useCallback(() => {
    setOpen(true);
  }, []);

  const renderModal = useCallback(() => {
    if (!open) return null;
    return (
      <FilterModalContent
        open={open}
        onOpenChange={setOpen}
        areaItems={areaItems}
        areaFilter={areaFilter}
        onAreaConfirm={setAreaFilter}
        facilityFilter={facilityFilter}
        onFacilityConfirm={setFacilityFilter}
        architectureFilter={architectureFilter}
        onArchitectureConfirm={setArchitectureFilter}
      />
    );
  }, [
    open,
    areaItems,
    areaFilter,
    setAreaFilter,
    facilityFilter,
    setFacilityFilter,
    architectureFilter,
    setArchitectureFilter,
  ]);

  return { openModal, renderModal };
}

function FilterModalContent({
  open,
  onOpenChange,
  facilityFilter,
  onFacilityConfirm,
  architectureFilter,
  onArchitectureConfirm,
  areaItems,
  areaFilter,
  onAreaConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  facilityFilter: Set<AccommodationFacilityType>;
  onFacilityConfirm: (filter: Set<AccommodationFacilityType>) => void;
  architectureFilter: Set<string>;
  onArchitectureConfirm: (filter: Set<string>) => void;
  areaItems: AreaItem[];
  areaFilter: Set<string>;
  onAreaConfirm: (filter: Set<string>) => void;
}) {
  const sortedAreaItems = useMemo(
    () => sortAreaItemsByDisplayOrder(areaItems),
    [areaItems],
  );

  const [areaDraft, setAreaDraft] = useState<Set<string>>(
    () => new Set(areaFilter),
  );
  const [facilityDraft, setFacilityDraft] = useState<
    Set<AccommodationFacilityType>
  >(() => new Set(facilityFilter));
  const [architectureDraft, setArchitectureDraft] = useState<Set<string>>(
    () => new Set(architectureFilter),
  );
  const largeDraft = isLargeFilter(architectureDraft);

  const handleAreaToggle = (slug: string) => {
    setAreaDraft((prev) => toggleSetItem(prev, slug));
  };
  const handleFacilityToggle = (type: AccommodationFacilityType) => {
    setFacilityDraft((prev) => toggleSetItem(prev, type));
  };
  const handleArchitectureToggle = (value: string) => {
    setArchitectureDraft((prev) => toggleSetItem(prev, value));
  };

  const handleClear = () => {
    setAreaDraft(new Set());
    setFacilityDraft(new Set());
    setArchitectureDraft(new Set());
  };

  const gtm = useGtm();

  const handleConfirm = () => {
    if (areaDraft.size > 0) {
      gtm.sendEvent("rsv_cal_area_filtered");
    }
    if (facilityDraft.size > 0) {
      gtm.sendEvent("rsv_cal_facility_filtered");
    }
    if (architectureDraft.size > 0) {
      gtm.sendEvent("rsv_cal_arch_type_filtered");
    }
    onAreaConfirm(areaDraft);
    onFacilityConfirm(facilityDraft);
    onArchitectureConfirm(architectureDraft);
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="sd-overlay" />
        <Dialog.Content
          className={clsx(
            "fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-40px)] -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-[30px]",
            "w-[calc(100vw-40px)] max-w-[375px]",
          )}
          aria-describedby={undefined}
        >
          <Dialog.Title className="sr-only">絞り込み</Dialog.Title>

          <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-white">
            {/* Scrollable content */}
            <div className="flex flex-col gap-[16px] overflow-y-auto p-[16px]">
              {/* エリアセクション */}
              {sortedAreaItems.length > 0 && (
                <div className="flex flex-col gap-[8px]">
                  <p className="font-notojp text-skogecha text-[16px] leading-[1.7] font-bold">
                    エリア
                  </p>
                  <div className="grid grid-cols-3">
                    {sortedAreaItems.map((item) => (
                      <FilterCheckbox
                        key={item.slug}
                        checked={areaDraft.has(item.slug)}
                        onChange={() => handleAreaToggle(item.slug)}
                        className="min-w-0"
                      >
                        <span className="font-notojp text-skogecha truncate text-[14px] leading-[1.7]">
                          {item.name}
                        </span>
                      </FilterCheckbox>
                    ))}
                  </div>
                </div>
              )}

              {/* オプションセクション */}
              <div className="flex flex-col gap-[8px]">
                <p className="font-notojp text-skogecha text-[16px] leading-[1.7] font-bold">
                  設備
                </p>
                <div className="flex items-start gap-[4px]">
                  {facilityFilterItems.map((item) => (
                    <FilterCheckbox
                      key={item.type}
                      checked={facilityDraft.has(item.type)}
                      onChange={() => handleFacilityToggle(item.type)}
                      className="flex-1"
                    >
                      <Image
                        src={item.icon}
                        alt={item.label}
                        width={24}
                        height={24}
                        className="size-[24px] shrink-0"
                      />
                    </FilterCheckbox>
                  ))}
                </div>
              </div>

              {/* 建築タイプセクション */}
              <div className="flex flex-col gap-[8px]">
                <p className="font-notojp text-skogecha text-[16px] leading-[1.7] font-bold">
                  建築タイプ
                </p>
                <div className="flex w-full items-start">
                  {/* 左列 */}
                  <div className="flex flex-[1_0_0] flex-col items-start">
                    {architectureFilterItems
                      .filter((_, i) => i % 2 === 0)
                      .map((item) => (
                        <FilterCheckbox
                          key={item.value}
                          checked={architectureDraft.has(item.value)}
                          onChange={() => handleArchitectureToggle(item.value)}
                        >
                          <span className="font-notojp text-skogecha text-[14px] leading-[1.7]">
                            {item.label}
                          </span>
                        </FilterCheckbox>
                      ))}
                  </div>
                  {/* 右列 */}
                  <div className="flex flex-[1_0_0] flex-col items-start">
                    {architectureFilterItems
                      .filter((_, i) => i % 2 === 1)
                      .map((item) => (
                        <FilterCheckbox
                          key={item.value}
                          checked={architectureDraft.has(item.value)}
                          onChange={() => handleArchitectureToggle(item.value)}
                        >
                          <span className="font-notojp text-skogecha text-[14px] leading-[1.7]">
                            {item.label}
                          </span>
                        </FilterCheckbox>
                      ))}
                  </div>
                </div>
                {/* Largeフィルター */}
                <div className="bg-skogecha-20 h-px" />
                <FilterCheckbox
                  checked={largeDraft}
                  onChange={() =>
                    handleArchitectureToggle(ARCHITECTURE_LARGE_VALUE)
                  }
                >
                  <span className="font-notojp text-skogecha text-[14px] leading-[1.7]">
                    Largeのみ表示
                  </span>
                </FilterCheckbox>
              </div>
            </div>

            {/* Footer */}
            <div className="border-skogecha/10 flex items-center gap-[8px] border-t px-[16px] py-[8px] shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
              <button
                data-analytics-id="reservation_calendar_clicked_filter_clear"
                onClick={handleClear}
                className="text-skogecha flex shrink-0 items-center justify-center rounded-full border border-[#c2c2c2] bg-white px-[24px] py-[12px] text-[14px] leading-none font-bold"
              >
                クリア
              </button>
              <button
                data-analytics-id="reservation_calendar_searched_filters"
                onClick={handleConfirm}
                className="bg-sorange flex flex-1 items-center justify-center rounded-[100px] px-[24px] py-[12px] text-[14px] leading-none font-bold text-white"
              >
                この条件で検索
              </button>
            </div>
          </div>

          {/* Close button */}
          <Dialog.Close
            className="flex size-[40px] shrink-0 items-center justify-center rounded-full bg-white"
            aria-label="モーダルを閉じる"
          >
            <Image src="/i/icon_close_x.svg" alt="" width={16} height={16} />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function FilterCheckbox({
  checked,
  onChange,
  className,
  children,
}: {
  checked: boolean;
  onChange: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label
      className={clsx(
        "flex cursor-pointer items-center gap-[4px] py-[8px]",
        className,
      )}
    >
      <CheckboxIcon checked={checked} />
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      {children}
    </label>
  );
}

function toggleSetItem<T>(set: ReadonlySet<T>, item: T): Set<T> {
  const next = new Set(set);
  if (next.has(item)) {
    next.delete(item);
  } else {
    next.add(item);
  }
  return next;
}
